import { LeafletProvider, createContainerComponent, createControlHook, createElementHook, createElementObject, extendContext, useLeafletContext } from './core';
import L from '@anlan/gs-map';
import type { Layer, LayersControl as LeafletLayersControl, LayersControlOptions } from '@anlan/gs-map';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FunctionComponent, ReactNode } from 'react';
import type { LeafletContextInterface } from './core';
const Control = L.Control;

export interface LayersControlProps extends LayersControlOptions {
    children?: ReactNode
}

export const useLayersControlElement = createElementHook(function createLayersControl(props: LayersControlProps, ctx: LeafletContextInterface) {
    const options = {
        ...props
    };
    delete options.children;
    const control = new Control.Layers(undefined, undefined, options);
    return createElementObject(control, extendContext(ctx, {
        layersControl: control
    }));
}, function updateLayersControl(control: LeafletLayersControl, props: LayersControlProps, prevProps: LayersControlProps) {
    if (props.collapsed !== prevProps.collapsed) {
        if (props.collapsed === true) {
            control.collapse();
        } else {
            control.expand();
        }
    }
});
export const useLayersControl = createControlHook(useLayersControlElement);

export interface ControlledLayerProps {
    checked?: boolean
    children: ReactNode
    name: string
}

type AddLayerFunc = (layersControl: LeafletLayersControl, layer: Layer, name: string) => void;

type LayersControlComponent = ReturnType<typeof createContainerComponent<LeafletLayersControl, LayersControlProps>> & {
    BaseLayer: FunctionComponent<ControlledLayerProps>
    Overlay: FunctionComponent<ControlledLayerProps>
};

export const LayersControl = createContainerComponent(useLayersControl) as LayersControlComponent;

export function createControlledLayer(addLayerToControl: AddLayerFunc): FunctionComponent<ControlledLayerProps> {
    return function ControlledLayer(props: ControlledLayerProps) {
        const parentContext = useLeafletContext();
        const propsRef = useRef(props);
        const [layer, setLayer] = useState<Layer | null>(null);
        const { layersControl, map } = parentContext;
        const addLayer = useCallback((layerToAdd: Layer) => {
            if (layersControl != null) {
                if (propsRef.current.checked) {
                    map.addLayer(layerToAdd);
                }
                addLayerToControl(layersControl, layerToAdd, propsRef.current.name);
                setLayer(layerToAdd);
            }
        }, [
            layersControl,
            map
        ]);
        const removeLayer = useCallback((layerToRemove: Layer) => {
            layersControl?.removeLayer(layerToRemove);
            setLayer(null);
        }, [
            layersControl
        ]);
        const context = useMemo<LeafletContextInterface>(() => {
            return extendContext(parentContext, {
                layerContainer: {
                    addLayer,
                    removeLayer
                }
            });
        }, [
            parentContext,
            addLayer,
            removeLayer
        ]);
        useEffect(() => {
            if (layer !== null && propsRef.current !== props) {
                if (props.checked === true && (propsRef.current.checked == null || propsRef.current.checked === false)) {
                    map.addLayer(layer);
                } else if (propsRef.current.checked === true && (props.checked == null || props.checked === false)) {
                    map.removeLayer(layer);
                }
                propsRef.current = props;
            }
        });
        return props.children ? /*#__PURE__*/ React.createElement(LeafletProvider, {
            value: context
        }, props.children) : null;
    };
}
LayersControl.BaseLayer = createControlledLayer(function addBaseLayer(layersControl, layer, name) {
    layersControl.addBaseLayer(layer, name);
});
LayersControl.Overlay = createControlledLayer(function addOverlay(layersControl, layer, name) {
    layersControl.addOverlay(layer, name);
});
